import {ID} from '@wandb/weave/common/util/id';
import produce from 'immer';
import {useCallback} from 'react';
import {useHistory} from 'react-router-dom';
import {Node} from 'slate';

import {parsedReportSpec} from '../../util/report';
import * as Urls from '../../util/urls';
import {openInNewTab} from '../../util/window';
import {useApolloClient} from '../hooks';
import {ApolloClient} from '../types';
import * as ViewApi from '../views/api';
import {PushReportOpts} from './types';

type EditReportOpts = PushReportOpts;

interface EditReportPanelGroupsParams {
  client: ApolloClient;
  entityName: string;
  projectName: string;
  reportID: string;
  opts?: EditReportOpts;
  editBlocks(blocks: Node[]): void;
  history: ReturnType<typeof useHistory>;
}

export async function editReportPanelGroups({
  client,
  entityName,
  projectName,
  reportID,
  editBlocks,
  opts,
  history,
}: EditReportPanelGroupsParams) {
  const loaded = await ViewApi.loadSingleViewById(client, reportID, 'no-cache');
  const viewType = loaded.type === 'runs' ? 'runs' : 'runs/draft';
  const spec = await parsedReportSpec(loaded.spec, viewType, client);
  const newSpec = {
    ...spec,
    blocks: produce(spec.blocks, editBlocks),
  };
  let draft;
  if (loaded.parentId == null) {
    draft = await ViewApi.save(client, {
      type: 'runs/draft',
      name: ID(12),
      displayName: loaded.displayName,
      description: loaded.description,
      spec: newSpec,
      previewUrl: loaded.previewUrl,
      coverUrl: loaded.coverUrl,
      project: {
        entityName,
        name: loaded.project?.name,
      },
      parentId: loaded.id,
    });
  } else {
    draft = await ViewApi.save(client, {...loaded, spec: newSpec});
  }
  const urlParams = {
    entityName,
    projectName,
    reportID: reportID,
    reportName: draft.displayName,
  };
  const url = Urls.reportEdit(urlParams, `?draftId=${draft.id}`);
  if (opts?.newTab) {
    openInNewTab(url);
  } else {
    history.push(url);
  }
}

type BaseAppendSectionToReportParams = {
  client: ApolloClient;
  entityName: string;
  projectName: string;
  reportID: string;
  opts?: EditReportOpts;
  history: ReturnType<typeof useHistory>;
};

type AppendSectionToReportParams = BaseAppendSectionToReportParams & {
  newBlock: Node;
};

export async function appendSectionToReport({
  client,
  entityName,
  projectName,
  reportID,
  newBlock,
  opts,
  history,
}: AppendSectionToReportParams) {
  return appendSectionsToReport({
    client,
    entityName,
    projectName,
    reportID,
    newBlocks: [newBlock],
    opts,
    history,
  });
}

type AppendSectionsToReportParams = BaseAppendSectionToReportParams & {
  newBlocks: Node[];
};

export async function appendSectionsToReport({
  client,
  entityName,
  projectName,
  reportID,
  newBlocks,
  opts,
  history,
}: AppendSectionsToReportParams) {
  return editReportPanelGroups({
    client,
    entityName,
    projectName,
    reportID,
    opts,
    history,
    editBlocks: blocks => {
      blocks.splice(-1, 0, ...newBlocks);
    },
  });
}

export function useAppendSectionToReport(
  entityName: string,
  projectName: string,
  reportID: string,
  opts?: EditReportOpts
) {
  const client = useApolloClient();
  const history = useHistory();

  return useCallback(
    (newBlock: Node) =>
      appendSectionToReport({
        client,
        entityName,
        projectName,
        reportID,
        newBlock,
        opts,
        history,
      }),
    [client, entityName, projectName, reportID, opts, history]
  );
}
